import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CameraIcon, VideoIcon, EyeIcon, ShieldIcon, ArrowRightIcon, CheckCircle2Icon, StoreIcon, Building2Icon, WarehouseIcon, HospitalIcon, CpuIcon, MoonIcon, HomeIcon, SmartphoneIcon, SearchIcon, ClipboardCheckIcon, PenToolIcon, WrenchIcon, HeadphonesIcon, TrendingDownIcon, ZapIcon, UsersIcon, HardDriveIcon, CloudIcon, ServerIcon, HelpCircleIcon, ChevronDownIcon } from 'lucide-react';
const services = [{
  icon: CameraIcon,
  title: 'Security Camera Installation',
  description: 'Professional installation of high-definition security cameras tailored to your facility layout and security needs.',
  color: 'text-blue-500',
  bg: 'bg-blue-50'
}, {
  icon: VideoIcon,
  title: 'CCTV Systems',
  description: 'Comprehensive Closed-Circuit Television systems providing reliable, continuous recording and local storage solutions.',
  color: 'text-indigo-500',
  bg: 'bg-indigo-50'
}, {
  icon: EyeIcon,
  title: '24/7 Remote Monitoring',
  description: 'Keep an eye on your business from anywhere in the world with secure, cloud-based remote monitoring access.',
  color: 'text-emerald-500',
  bg: 'bg-emerald-50'
}, {
  icon: ShieldIcon,
  title: 'Surveillance System Design',
  description: 'Custom-designed surveillance architectures that eliminate blind spots and integrate seamlessly with your network.',
  color: 'text-purple-500',
  bg: 'bg-purple-50'
}];
const industries = [{
  icon: StoreIcon,
  name: 'Retail',
  desc: 'Prevent loss and monitor customer traffic patterns.'
}, {
  icon: Building2Icon,
  name: 'Corporate',
  desc: 'Secure office spaces and manage employee safety.'
}, {
  icon: WarehouseIcon,
  name: 'Warehousing',
  desc: 'Monitor inventory and logistics operations 24/7.'
}, {
  icon: HospitalIcon,
  name: 'Healthcare',
  desc: 'Ensure patient safety and secure restricted areas.'
}, {
  icon: HomeIcon,
  name: 'Residential',
  desc: 'Protect your home and family with smart surveillance.'
}];
const advancedFeatures = [{
  icon: CpuIcon,
  title: 'AI Analytics',
  desc: 'Facial recognition, object detection, and behavior analysis.'
}, {
  icon: MoonIcon,
  title: 'Night Vision',
  desc: 'Crystal clear imaging even in total darkness with IR technology.'
}, {
  icon: SmartphoneIcon,
  title: 'Mobile Integration',
  desc: 'View live feeds and receive alerts directly on your smartphone.'
}, {
  icon: SearchIcon,
  title: 'Smart Search',
  desc: 'Quickly find specific incidents with intelligent playback tools.'
}];
const processSteps = [{
  icon: ClipboardCheckIcon,
  title: 'Audit',
  desc: 'We assess your current security and identify vulnerabilities.'
}, {
  icon: PenToolIcon,
  title: 'Design',
  desc: 'Custom surveillance blueprint tailored to your facility.'
}, {
  icon: WrenchIcon,
  title: 'Install',
  desc: 'Professional setup by certified security technicians.'
}, {
  icon: HeadphonesIcon,
  title: 'Support',
  desc: 'Ongoing maintenance and 24/7 technical assistance.'
}];
export function VideoMonitoringPage({
  onNavigate
}: {
  onNavigate: (page: string) => void;
}) {
  const [activeFaq, setActiveFaq] = useState<number | null>(null);
  return <div className="bg-white" data-id="element-2104">
      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden bg-slate-900" data-id="element-2105">
        <div className="absolute inset-0 bg-grid-pattern opacity-20" data-id="element-2106"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-slate-900/90" data-id="element-2107"></div>
        <div className="absolute top-0 right-1/4 -translate-y-1/4 w-[600px] h-[600px] bg-blue-500/10 rounded-full blur-[120px] pointer-events-none" data-id="element-2108"></div>

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2109">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-2110">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white text-sm font-medium mb-6 backdrop-blur-sm" data-id="element-2111">
              <CameraIcon className="w-4 h-4 mr-2 text-blue-400" data-id="element-2112" />
              Video & Monitoring Solutions
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-4 tracking-tight" data-id="element-2113">
              Advanced
              <br className="hidden md:block" data-id="element-2114" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-accent-dark" data-id="element-2115">
                Video Monitoring
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto leading-relaxed mb-10" data-id="element-2116">
              From HD to 5K AI camera installations to 24/7 remote monitoring,
              we provide end-to-end video security solutions to keep your assets
              safe.
            </p>
            <button onClick={() => onNavigate('home')} className="bg-accent hover:bg-accent-dark text-white px-8 py-4 rounded-full font-bold text-lg transition-colors shadow-lg inline-flex items-center" data-id="element-2117">
              Secure Your Facility
              <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-2118" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-slate-50" data-id="element-2119">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2120">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-2121">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-2122">
              Comprehensive Video Solutions
            </h2>
            <p className="text-lg text-slate-600" data-id="element-2123">
              We design, install, and maintain state-of-the-art video monitoring
              systems tailored to your specific operational needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2124">
            {services.map((service, i) => {
            const Icon = service.icon;
            return <motion.div key={i} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: i * 0.1
            }} className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-2125">
                  <div className={`w-14 h-14 ${service.bg} rounded-2xl flex items-center justify-center mb-6`} data-id="element-2126">
                    <Icon className={`w-7 h-7 ${service.color}`} data-id="element-2127" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-3" data-id="element-2128">
                    {service.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed" data-id="element-2129">
                    {service.description}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* ROI of Video Security */}
      <section className="py-24 bg-slate-900 text-white relative overflow-hidden" data-id="element-2130">
        <div className="absolute inset-0 bg-grid-pattern opacity-10" data-id="element-2131"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-2132">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-2133">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-2134">
              The ROI of Professional Video Security
            </h2>
            <p className="text-lg text-slate-400" data-id="element-2135">
              A modern surveillance system pays for itself by reducing loss and
              increasing operational efficiency.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-2136">
            {[{
            icon: TrendingDownIcon,
            stat: '67%',
            desc: 'Average reduction in internal and external theft.'
          }, {
            icon: ShieldIcon,
            stat: '45%',
            desc: 'Potential reduction in business insurance premiums.'
          }, {
            icon: ZapIcon,
            stat: '80%',
            desc: 'Faster incident resolution with smart search tools.'
          }, {
            icon: UsersIcon,
            stat: '24/7',
            desc: 'Continuous facility coverage without adding security staff.'
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="bg-white/5 border border-white/10 rounded-2xl p-8 text-center" data-id="element-2137">
                <item.icon className="w-10 h-10 text-blue-400 mx-auto mb-4" data-id="element-2138" />
                <div className="text-4xl font-black text-white mb-2" data-id="element-2139">
                  {item.stat}
                </div>
                <p className="text-slate-400 text-sm leading-relaxed" data-id="element-2140">
                  {item.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Advanced Features */}
      <section className="py-24 bg-white" data-id="element-2141">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2142">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-2143">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} data-id="element-2144">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" data-id="element-2145">
                Next-Generation Security Features
              </h2>
              <p className="text-lg text-slate-600 mb-8" data-id="element-2146">
                Our systems go beyond simple recording. We leverage the latest
                in AI and cloud technology to provide proactive security that
                works for you.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-2147">
                {advancedFeatures.map((feature, i) => <div key={i} className="flex gap-4" data-id="element-2148">
                    <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center" data-id="element-2149">
                      <feature.icon className="w-5 h-5 text-primary" data-id="element-2150" />
                    </div>
                    <div data-id="element-2151">
                      <h4 className="font-bold text-slate-900" data-id="element-2152">
                        {feature.title}
                      </h4>
                      <p className="text-sm text-slate-500" data-id="element-2153">{feature.desc}</p>
                    </div>
                  </div>)}
              </div>
            </motion.div>
            <div className="relative" data-id="element-2154">
              <img src="https://images.unsplash.com/photo-1557597774-9d273605dfa9?auto=format&fit=crop&q=80&w=800" alt="Security Monitoring" className="rounded-3xl shadow-2xl" data-id="element-2155" />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-2xl shadow-xl border border-slate-100 hidden md:block" data-id="element-2156">
                <div className="flex items-center gap-4" data-id="element-2157">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center" data-id="element-2158">
                    <CheckCircle2Icon className="w-6 h-6 text-green-600" data-id="element-2159" />
                  </div>
                  <div data-id="element-2160">
                    <p className="text-sm font-bold text-slate-900" data-id="element-2161">
                      AI Enabled
                    </p>
                    <p className="text-xs text-slate-500" data-id="element-2162">
                      Real-time threat detection
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Storage & Recording Options */}
      <section className="py-24 bg-slate-50" data-id="element-2163">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2164">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-2165">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-2166">
              Flexible Storage Solutions
            </h2>
            <p className="text-lg text-slate-600" data-id="element-2167">
              Choose how and where your video footage is stored based on your
              retention requirements and budget.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2168">
            {[{
            icon: HardDriveIcon,
            title: 'Local NVR',
            desc: 'Store footage on-site using a Network Video Recorder. Best for high-camera counts and avoiding monthly cloud storage fees.',
            pros: ['No monthly fees', 'High capacity', 'Works without internet']
          }, {
            icon: CloudIcon,
            title: 'Cloud Storage',
            desc: 'Footage is securely uploaded to the cloud. Best for multi-site businesses and protecting footage from physical theft or damage.',
            pros: ['Access anywhere', 'Disaster proof', 'Easy scalability']
          }, {
            icon: ServerIcon,
            title: 'Hybrid Solution',
            desc: 'The best of both worlds. Store high-res footage locally while backing up critical events and low-res streams to the cloud.',
            pros: ['Maximum reliability', 'Bandwidth efficient', 'Redundant backup']
          }].map((option, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm" data-id="element-2169">
                <div className="w-14 h-14 bg-blue-50 rounded-xl flex items-center justify-center mb-6" data-id="element-2170">
                  <option.icon className="w-7 h-7 text-blue-600" data-id="element-2171" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-2172">
                  {option.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed mb-6" data-id="element-2173">
                  {option.desc}
                </p>
                <ul className="space-y-2" data-id="element-2174">
                  {option.pros.map((pro, j) => <li key={j} className="flex items-center text-sm text-slate-700 font-medium" data-id="element-2175">
                      <CheckCircle2Icon className="w-4 h-4 text-green-500 mr-2" data-id="element-2176" />
                      {pro}
                    </li>)}
                </ul>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Industry Applications */}
      <section className="py-24 bg-slate-900 text-white" data-id="element-2177">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2178">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-2179">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-2180">
              Industry Specific Solutions
            </h2>
            <p className="text-slate-400" data-id="element-2181">
              Every industry has unique security challenges. We provide tailored
              surveillance strategies for your specific sector.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-8" data-id="element-2182">
            {industries.map((industry, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="bg-white/5 border border-white/10 p-8 rounded-2xl hover:bg-white/10 transition-colors" data-id="element-2183">
                <industry.icon className="w-10 h-10 text-white mb-6" data-id="element-2184" />
                <h3 className="text-xl font-bold mb-3" data-id="element-2185">{industry.name}</h3>
                <p className="text-slate-400 text-sm leading-relaxed" data-id="element-2186">
                  {industry.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-24 bg-white" data-id="element-2187">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2188">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-2189">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-2190">
              Our Implementation Process
            </h2>
            <p className="text-lg text-slate-600" data-id="element-2191">
              From initial audit to ongoing support, we ensure your security
              transition is seamless.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8" data-id="element-2192">
            {processSteps.map((step, i) => <div key={i} className="relative text-center" data-id="element-2193">
                {i < processSteps.length - 1 && <div className="hidden md:block absolute top-10 left-1/2 w-full h-0.5 bg-slate-100 -z-10" data-id="element-2194"></div>}
                <div className="w-20 h-20 bg-white border-4 border-slate-50 rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm" data-id="element-2195">
                  <step.icon className="w-8 h-8 text-primary" data-id="element-2196" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-2197">
                  {step.title}
                </h3>
                <p className="text-slate-500 text-sm" data-id="element-2198">{step.desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Video Security FAQ */}
      <section className="py-24 bg-slate-50" data-id="element-2199">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2200">
          <div className="text-center mb-16" data-id="element-2201">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 flex items-center justify-center gap-3" data-id="element-2202">
              <HelpCircleIcon className="w-8 h-8 text-primary" data-id="element-2203" />
              Video Security FAQ
            </h2>
            <p className="text-lg text-slate-600" data-id="element-2204">
              Common questions about our surveillance systems.
            </p>
          </div>
          <div className="space-y-4" data-id="element-2205">
            {[{
            q: 'How long is footage stored?',
            a: 'Retention times are fully customizable. Depending on the storage capacity you choose (NVR hard drive size or cloud plan), you can store footage anywhere from 7 days to 1+ years.'
          }, {
            q: 'Can I view cameras remotely?',
            a: 'Yes. All our modern systems come with secure mobile apps and web portals, allowing authorized users to view live feeds and recorded footage from anywhere in the world.'
          }, {
            q: 'Do the cameras work at night?',
            a: 'Absolutely. We install cameras equipped with advanced Infrared (IR) technology and low-light sensors to provide clear, crisp imaging even in total darkness.'
          }, {
            q: 'What internet speed do I need?',
            a: 'It depends on the number of cameras and whether you use cloud storage. Local NVRs use very little internet bandwidth, while cloud cameras require higher upload speeds. We will assess your network during the site audit.'
          }, {
            q: 'Can I add more cameras later?',
            a: 'Yes, our systems are highly scalable. We can design the initial infrastructure with future expansion in mind, making it easy to add cameras as your business grows.'
          }].map((faq, i) => <div key={i} className="border border-slate-200 rounded-2xl overflow-hidden bg-white" data-id="element-2206">
                <button onClick={() => setActiveFaq(activeFaq === i ? null : i)} className="w-full flex items-center justify-between p-6 text-left hover:bg-slate-50 transition-colors" data-id="element-2207">
                  <span className="font-bold text-slate-900" data-id="element-2208">{faq.q}</span>
                  <motion.div animate={{
                rotate: activeFaq === i ? 180 : 0
              }} data-id="element-2209">
                    <ChevronDownIcon className="w-5 h-5 text-slate-400" data-id="element-2210" />
                  </motion.div>
                </button>
                <AnimatePresence data-id="element-2211">
                  {activeFaq === i && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} className="overflow-hidden" data-id="element-2212">
                      <div className="p-6 pt-0 text-slate-600 border-t border-slate-100" data-id="element-2213">
                        {faq.a}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary py-20 relative overflow-hidden" data-id="element-2214">
        <div className="absolute inset-0 bg-grid-pattern opacity-10" data-id="element-2215"></div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center" data-id="element-2216">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-2217">
            Ready to upgrade your security?
          </h2>
          <p className="text-xl text-blue-100 mb-10" data-id="element-2218">
            Contact us today for a free site walk-through and custom
            surveillance system design.
          </p>
          <button onClick={() => onNavigate('contact')} className="bg-white text-primary px-10 py-4 rounded-full font-bold text-lg hover:bg-slate-50 transition-all shadow-xl inline-flex items-center" data-id="element-2219">
            Schedule Consultation
            <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-2220" />
          </button>
        </div>
      </section>
    </div>;
}